import { tokens } from 'styling/tokens'
import { IStatusPill } from 'types/ui/statusPill.interface'
import {
  DeliveryDisplayState,
  GradeBasis,
  IMaterialAuditEntry,
  IMaterialDelivery,
  IDeliveryYield,
  MaterialReceiptStation,
} from 'types/materialReceipt.interface'

export const STATES: DeliveryDisplayState[] = ['DRAFT', 'ARRIVED', 'IN_PROGRESS', 'COMPLETED', 'FINALIZED']

export const STATE_META: {
  [k in DeliveryDisplayState]: { label: string; color: string; tone: IStatusPill['status']; blurb: string }
} = {
  DRAFT: { label: 'Draft', color: tokens.text.muted, tone: 'neutral', blurb: 'Pre-shipment sample logged at source' },
  ARRIVED: { label: 'Arrived', color: tokens.status.info.fg, tone: 'info', blurb: 'Gross weight captured at weighbridge' },
  IN_PROGRESS: { label: 'In progress', color: tokens.accent.base, tone: 'accent', blurb: 'Awaiting tare weigh and/or lab assay (independent)' },
  COMPLETED: { label: 'Completed', color: tokens.status.warning.fg, tone: 'warning', blurb: 'Tare and assay both in — yield calculated' },
  FINALIZED: { label: 'Finalized', color: tokens.status.success.fg, tone: 'success', blurb: 'Locked. Corrections require reopen' },
}

export const SUPPLIERS = ['Sabel Mining Co.', 'Korrah Haulage Ltd.', 'Internal — North Pit', 'Tendai Contractors']
export const SOURCES = ['North Pit Bench 4', 'South Stockpile A', 'Reclaim Dam 2', 'Third-party ROM']

export const pending = (t: IMaterialDelivery): ('tare' | 'assay')[] => {
  const needs: ('tare' | 'assay')[] = []
  if (t.tare == null) needs.push('tare')
  if (t.moisture == null || t.grade == null) needs.push('assay')
  return needs
}

export const deriveState = (t: IMaterialDelivery): DeliveryDisplayState => {
  if (t.state === 'FINALIZED') return 'FINALIZED'
  if (t.gross == null) return 'DRAFT'
  const need = pending(t)
  if (need.length === 0) return 'COMPLETED'
  const startedDownstream = t.tare != null || t.moisture != null || t.grade != null
  return startedDownstream ? 'IN_PROGRESS' : 'ARRIVED'
}

export const computeYield = (t: IMaterialDelivery, basis: GradeBasis): IDeliveryYield | null => {
  const { gross, tare, moisture, grade } = t
  if (gross == null || tare == null || moisture == null || grade == null) return null
  const netWet = +(gross - tare).toFixed(3)
  const netDry = +(netWet * (1 - moisture / 100)).toFixed(3)
  const gold = basis === 'DRY' ? +(netDry * grade).toFixed(1) : +(netWet * grade).toFixed(1)
  return { netWet, netDry, gold }
}

export const discrepancyOf = (t: IMaterialDelivery, threshold: number) => {
  if (t.preGrade == null || t.grade == null) return null
  const pct = ((t.grade - t.preGrade) / t.preGrade) * 100
  return { pct: +pct.toFixed(1), breach: Math.abs(pct) > threshold }
}

// What this web station can do on a ticket right now. Gross/tare are mobile-only, so the web
// never offers them — Lab enters assay, Manager finalizes (and may enter assay as a fallback).
export const taskForStation = (
  t: IMaterialDelivery,
  station: MaterialReceiptStation,
): { label: string; kind: 'assay' | 'finalize' } | null => {
  if (deriveState(t) === 'FINALIZED') return null
  if (t.gross == null) return null // awaiting weighbridge (mobile)
  const need = pending(t)
  if (need.includes('assay')) return { label: 'Enter assay', kind: 'assay' }
  if (need.includes('tare')) return null // awaiting weighbridge tare (mobile)
  if (need.length === 0 && station === 'MANAGER') return { label: 'Finalize', kind: 'finalize' }
  return null
}

export const fmt = (n: number | null | undefined, d = 1) =>
  n == null ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })

export const now = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

const daysAgo = (n: number) => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d.toISOString().slice(0, 10)
}

const shiftTime = (hhmm: string, mins: number) => {
  const [h, m] = (hhmm || '06:00').split(':').map(Number)
  let total = h * 60 + m + mins
  total = ((total % 1440) + 1440) % 1440
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

const OP = { Source: 'John Siziba', Weighbridge: 'Bill Simbeya', Lab: 'Grace Okafor', Manager: 'S. Banda' }

export const seedDeliveries = (): IMaterialDelivery[] => {
  const buildProv = (o: Partial<IMaterialDelivery> & { preGrade?: number | null }) => {
    const prov: IMaterialDelivery['prov'] = {}
    const t0 = o.arrived || '06:00'
    if (o.preGrade != null) prov.preShipment = { at: shiftTime(t0, -120), by: 'Source', name: OP.Source }
    if (o.gross != null) prov.gross = { at: o.arrived || t0, by: 'Weighbridge', name: OP.Weighbridge }
    if (o.tare != null) prov.tare = { at: shiftTime(o.arrived || t0, 35), by: 'Weighbridge', name: OP.Weighbridge }
    if (o.moisture != null && o.grade != null) prov.assay = { at: shiftTime(o.arrived || t0, 95), by: 'Lab', name: OP.Lab }
    if (o.state === 'FINALIZED') prov.finalized = { at: shiftTime(o.arrived || t0, 120), by: 'Manager', name: OP.Manager }
    return prov
  }
  const t = (o: Partial<IMaterialDelivery>): IMaterialDelivery =>
    ({
      moisture: null, grade: null, gross: null, tare: null, driver: null, waybill: null,
      fleetLinked: false, corrections: [], prov: buildProv(o), ...o,
    } as IMaterialDelivery)
  return [
    t({ id: 'DLV-2046', plate: 'FST118', supplier: 'Korrah Haulage Ltd.', source: 'Third-party ROM', preGrade: 5.0, state: 'DRAFT', date: daysAgo(0) }),
    t({ id: 'DLV-2045', plate: 'EQR492', supplier: 'Sabel Mining Co.', source: 'North Pit Bench 4', preGrade: 4.6, gross: 40.0, driver: 'K. Phiri', waybill: 'WB-99825', state: 'OPEN', arrived: '08:30', date: daysAgo(0) }),
    t({ id: 'DLV-2044', plate: 'DPL063', supplier: 'Tendai Contractors', source: 'Reclaim Dam 2', preGrade: 2.4, gross: 36.1, tare: 13.2, driver: 'T. Sibanda', waybill: 'WB-99824', state: 'OPEN', arrived: '07:55', date: daysAgo(0) }),
    t({ id: 'DLV-2047', plate: 'JKP330', supplier: 'Sabel Mining Co.', source: 'North Pit Bench 4', preGrade: 4.1, gross: 38.0, moisture: 11.0, grade: 4.0, driver: 'K. Phiri', waybill: 'WB-99826', state: 'OPEN', arrived: '08:05', date: daysAgo(0) }),
    t({ id: 'DLV-2043', plate: 'CMN771', supplier: 'Internal — North Pit', source: 'South Stockpile A', preGrade: 3.8, gross: 39.9, tare: 14.6, moisture: 10.1, grade: 3.9, driver: 'A. Dube', waybill: 'WB-99823', state: 'OPEN', fleetLinked: true, arrived: '07:20', date: daysAgo(0) }),
    t({ id: 'DLV-2042', plate: 'BTR205', supplier: 'Korrah Haulage Ltd.', source: 'Third-party ROM', preGrade: 5.1, gross: 41.2, tare: 15.0, moisture: 13.2, grade: 3.6, driver: 'P. Ncube', waybill: 'WB-99822', state: 'FINALIZED', flagged: true, arrived: '06:48', date: daysAgo(2) }),
    t({ id: 'DLV-2041', plate: 'AHK418', supplier: 'Sabel Mining Co.', source: 'North Pit Bench 4', preGrade: 4.2, gross: 38.4, tare: 14.1, moisture: 11.5, grade: 4.0, driver: 'J. Moyo', waybill: 'WB-99821', state: 'FINALIZED', fleetLinked: true, arrived: '06:12', date: daysAgo(2) }),
    t({ id: 'DLV-2039', plate: 'AHK418', supplier: 'Sabel Mining Co.', source: 'North Pit Bench 4', preGrade: 4.4, gross: 39.1, tare: 14.3, moisture: 12.0, grade: 4.3, driver: 'J. Moyo', waybill: 'WB-99819', state: 'FINALIZED', fleetLinked: true, arrived: '06:30', date: daysAgo(5) }),
    t({ id: 'DLV-2037', plate: 'GHT902', supplier: 'Internal — North Pit', source: 'South Stockpile A', preGrade: 3.6, gross: 37.8, tare: 13.9, moisture: 9.4, grade: 3.7, driver: 'A. Dube', waybill: 'WB-99817', state: 'FINALIZED', arrived: '07:05', date: daysAgo(9) }),
    t({ id: 'DLV-2034', plate: 'BTR205', supplier: 'Korrah Haulage Ltd.', source: 'Third-party ROM', preGrade: 4.8, gross: 40.6, tare: 14.8, moisture: 13.9, grade: 4.6, driver: 'P. Ncube', waybill: 'WB-99814', state: 'FINALIZED', arrived: '06:55', date: daysAgo(23) }),
  ]
}

export const seedAudit = (): IMaterialAuditEntry[] => [
  { id: 1, ticket: 'DLV-2041', action: 'Finalize', actor: 'S. Banda (Manager)', at: '06:40', reason: null, from: 'COMPLETED', to: 'FINALIZED' },
  { id: 2, ticket: 'DLV-2042', action: 'Case opened', actor: 'SYSTEM', at: '06:50', reason: 'Grade −29.4% vs pre-shipment', from: null, to: null },
  { id: 3, ticket: 'DLV-2042', action: 'Finalize', actor: 'S. Banda (Manager)', at: '07:02', reason: 'Reviewed; material confirmed low-grade', from: 'COMPLETED', to: 'FINALIZED' },
]
